import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SPOTLIGHT } from "@/lib/data";

const ease = [0.22, 1, 0.36, 1];

export default function Spotlight() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section
      ref={ref}
      data-testid="spotlight-section"
      className="relative bg-[color:var(--archive)] text-[color:var(--bone)] overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid grid-cols-12 gap-8 md:gap-10">
        {/* Left: portrait */}
        <div className="col-span-12 md:col-span-6 lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, ease }}
            className="relative"
          >
            <div className="archway aspect-[4/5] overflow-hidden border border-[color:var(--archive-line)] relative">
              <motion.img
                style={{ y, scale }}
                src={SPOTLIGHT.image}
                alt={SPOTLIGHT.house}
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 30%, rgba(0,0,0,0) 30%, rgba(13,17,23,0.65) 100%)",
                }}
              />
            </div>
            <div className="mt-4 flex items-center justify-between numeric-index text-[color:var(--bone)]/60">
              <span>Plate № 042</span>
              <span>{SPOTLIGHT.years}</span>
            </div>
          </motion.div>
        </div>

        {/* Right: content */}
        <div className="col-span-12 md:col-span-6 lg:col-span-7 md:pl-6 lg:pl-16">
          <div className="overline text-[color:var(--bone)]/60 mb-6">§ Family Spotlight</div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.0, ease }}
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(38px, 5vw, 76px)" }}
          >
            {SPOTLIGHT.house.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="font-italic-serif italic text-[color:var(--bone)]/70">
              {SPOTLIGHT.house.split(" ").slice(-1)[0]}
            </span>
          </motion.h2>

          <div className="mt-4 numeric-index text-[color:var(--bone)]/60">{SPOTLIGHT.region}</div>

          <p className="mt-8 text-[16px] md:text-[18px] leading-[1.65] max-w-xl text-[color:var(--bone)]/85">
            {SPOTLIGHT.intro}
          </p>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-[color:var(--archive-line)]">
            {SPOTLIGHT.stats.map((s) => (
              <div
                key={s.k}
                className="border-r border-b border-[color:var(--archive-line)] p-5 md:p-6"
              >
                <div className="font-serif-display" style={{ fontSize: "clamp(28px, 3vw, 42px)" }}>
                  {s.v}
                </div>
                <div className="numeric-index text-[color:var(--bone)]/60 mt-2">{s.k}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="/category/historical-families"
              className="btn-rust"
              style={{ background: "var(--bone)", color: "var(--archive)" }}
              data-testid="spotlight-read-more"
            >
              Read the family record <span aria-hidden>→</span>
            </a>
            <a
              href="/category/family-tree"
              data-testid="spotlight-tree"
              className="btn-ghost"
              style={{ borderColor: "var(--bone)", color: "var(--bone)" }}
            >
              View the tree
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
